import { fetchWithContext } from "../../../utils/fetch";

/**
 * Client for the ported cluster metrics read API (GET /api/cluster/{name}/metrics), which serves
 * the same dashboard payload V2's metrics tab renders: one series per metric per resource, each
 * a list of time/value points already downsampled server-side by the requested interval.
 */

export interface MetricPoint {
  time: string;
  value: number;
}

export interface MetricSeries {
  id: string;
  name: string;
  metricId: string;
  metricName: string;
  resource: string;
  points: MetricPoint[];
}

export interface MetricsDashboard {
  schemaVersion: number;
  id: string;
  name: string;
  cluster: string;
  organizationId: string;
  from: string;
  to: string;
  interval: string;
  series: MetricSeries[];
}

/** V2's time-range choices, in the order its dropdown lists them. */
export const TIME_RANGES = [
  { label: "Last 24 hours", hours: 24 },
  { label: "Last 7 days", hours: 24 * 7 },
  { label: "Last 30 days", hours: 24 * 30 },
  { label: "Last 3 months", hours: 24 * 90 },
] as const;

/** V2's interval choices. The backend accepts only these three. */
export const INTERVALS = [
  { label: "5 minutes", value: "5m" },
  { label: "1 hour", value: "1h" },
  { label: "1 day", value: "1d" },
] as const;

/**
 * The backend caps a request at 2880 hours and rejects anything longer with a 400, so a long
 * range at a fine interval is not just slow, it fails. V2 leaves that to the user; picking a
 * sane default interval per range avoids the error without taking the choice away.
 */
export function defaultIntervalForRange(hours: number): string {
  if (hours <= 24) return "5m";
  if (hours <= 24 * 7) return "1h";
  return "1d";
}

export function rangeToIsoWindow(hours: number, now: Date = new Date()) {
  const to = new Date(now.getTime());
  const from = new Date(now.getTime() - hours * 3600 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

export async function fetchClusterMetrics(
  context: object,
  headers: object,
  clusterName: string,
  from: string,
  to: string,
  interval: string,
  organizationId?: string
): Promise<MetricsDashboard> {
  const params = new URLSearchParams({ from, to, interval });
  if (organizationId) {
    params.set("maybeOrganizationId", organizationId);
  }
  return fetchWithContext(
    `/cluster/${encodeURIComponent(clusterName)}/metrics?${params.toString()}`,
    context,
    { headers }
  );
}

/** The four metric families the backend returns, in the order V2's dashboard stacks them. */
export const METRIC_ORDER = [
  "redis-cpu-percent",
  "redis-memory-percent",
  "pod-cpu-percent",
  "pod-memory-percent",
] as const;

export interface MetricGroup {
  metricId: string;
  metricName: string;
  series: MetricSeries[];
}

/**
 * One card per metric family, each holding every resource's series. Unknown metric ids are kept
 * and appended rather than dropped, so a new metric added server-side still shows up.
 */
export function groupByMetric(series: MetricSeries[]): MetricGroup[] {
  const byId = new Map<string, MetricGroup>();
  for (const s of series) {
    const existing = byId.get(s.metricId);
    if (existing) {
      existing.series.push(s);
    } else {
      byId.set(s.metricId, {
        metricId: s.metricId,
        metricName: s.metricName,
        series: [s],
      });
    }
  }
  const known = METRIC_ORDER.filter((id) => byId.has(id)).map(
    (id) => byId.get(id) as MetricGroup
  );
  const extra: MetricGroup[] = [];
  byId.forEach((group) => {
    if (!(METRIC_ORDER as readonly string[]).includes(group.metricId)) {
      extra.push(group);
    }
  });
  const all = known.concat(extra);
  all.forEach((group: MetricGroup) =>
    group.series.sort((a: MetricSeries, b: MetricSeries) =>
      a.resource.localeCompare(b.resource)
    )
  );
  return all;
}
